import { useCallback, useRef, useState } from 'react'

/**
 * Arrastrar y soltar archivos, sin el componente alrededor.
 *
 * Estaba dentro de `ImageUploader`, atado a "una imagen, que se sube a Wasabi". La bandeja
 * de la migración necesita lo mismo —resaltar al arrastrar, aceptar el soltar, abrir el
 * selector al hacer clic— pero con N archivos de cualquier tipo y sin subir nada por su
 * cuenta. Copiar aquellas 80 líneas habría dejado dos versiones que se separan a la primera
 * corrección, así que se extrae el comportamiento y se quedan los dos con el mismo.
 *
 * Tres detalles que parecen menores y no lo son:
 *  - `dragleave` se dispara también al pasar por encima de un hijo. Se lleva la cuenta con
 *    un contador en vez de apagar el resaltado al primer `leave`, que es lo que hace que
 *    parpadee mientras el usuario mueve el archivo por encima.
 *  - Se limpia el `value` del input tras elegir, o volver a soltar EL MISMO archivo no
 *    dispara `change` y parece que la aplicación se colgó.
 *  - El clic en la zona abre el selector: hay gente que no arrastra, y una zona que solo
 *    acepta arrastrar es una zona que la mitad de la gente cree que está rota.
 *
 * @param {(archivos: File[]) => void} onArchivos
 * @param {{disabled?: boolean, multiple?: boolean, accept?: string}} opts
 */
export function useDropzone(onArchivos, opts = {}) {
  const { disabled = false, multiple = true, accept } = opts
  const [arrastrando, setArrastrando] = useState(false)
  const contador = useRef(0)
  const inputRef = useRef(null)

  const entregar = useCallback(
    (lista) => {
      const archivos = Array.from(lista || []).filter(Boolean)
      if (archivos.length > 0) onArchivos?.(multiple ? archivos : archivos.slice(0, 1))
    },
    [onArchivos, multiple]
  )

  const onDragEnter = useCallback(
    (e) => {
      e.preventDefault()
      e.stopPropagation()
      if (disabled) return
      contador.current += 1
      setArrastrando(true)
    },
    [disabled]
  )

  const onDragOver = useCallback(
    (e) => {
      // Sin esto el navegador abre el archivo en una pestaña en vez de dejarlo soltar.
      e.preventDefault()
      e.stopPropagation()
    },
    []
  )

  const onDragLeave = useCallback((e) => {
    e.preventDefault()
    e.stopPropagation()
    contador.current = Math.max(0, contador.current - 1)
    if (contador.current === 0) setArrastrando(false)
  }, [])

  const onDrop = useCallback(
    (e) => {
      e.preventDefault()
      e.stopPropagation()
      contador.current = 0
      setArrastrando(false)
      if (disabled) return
      entregar(e.dataTransfer?.files)
    },
    [disabled, entregar]
  )

  const abrirSelector = useCallback(() => {
    if (!disabled) inputRef.current?.click()
  }, [disabled])

  const onChangeInput = useCallback(
    (e) => {
      entregar(e.target.files)
      // Sin esto, volver a elegir el MISMO archivo no dispara `change`.
      e.target.value = ''
    },
    [entregar]
  )

  return {
    arrastrando,
    /** Se esparce sobre el contenedor de la zona. */
    propsZona: { onDragEnter, onDragOver, onDragLeave, onDrop, onClick: abrirSelector },
    /** Se esparce sobre un `<input type="file" hidden>`. */
    propsInput: { ref: inputRef, type: 'file', multiple, accept, hidden: true, onChange: onChangeInput },
    abrirSelector,
  }
}

export default useDropzone
